/**
 * @file Persistent aim lasers for precision rifles.
 *
 * The weapon owns the visual recipe and the weapon system owns the aim vector.
 * This presentation system only turns those facts into a beam in the scene, so
 * the same Sniper telegraph works for the player, enemies, and any future body
 * that equips the archetype.
 */

import * as THREE from 'three';
import { System } from '../ecs/System.js';
import { Collider, Health, Transform, Weapon } from '../components/index.js';
import { Events } from '../core/EventBus.js';
import { raycastAABB } from '../physics/AABB.js';

const DEFAULT_WIDTH = 0.04;
const DEFAULT_OPACITY = 0.7;
const RENDER_ORDER = 9;

export class SniperLaserSystem extends System {
  static priority = 95;

  constructor() {
    super();
    /** @type {Map<import('../ecs/Entity.js').Entity, THREE.Mesh>} @private */
    this._active = new Map();
    /** @type {THREE.BoxGeometry | null} Shared unit beam geometry. @private */
    this._geometry = null;
    /**
     * Static blockers, gathered once per frame and shared by every beam. The
     * set is identical for all carriers; only the per-weapon layer test below
     * differs, and that is too cheap to justify a second pass over the level.
     * @type {import('../ecs/Entity.js').Entity[]}
     * @private
     */
    this._blockers = [];
    /** @type {(() => void)[]} @private */
    this._subscriptions = [];
  }

  /** @returns {void} */
  init() {
    this._geometry = new THREE.BoxGeometry(1, 1, 1);
    this._subscriptions.push(
      this.ctx.events.on(Events.ENTITY_DESTROYED, (entity) => this._release(entity))
    );
  }

  /**
   * @param {number} _dt Delta time in seconds.
   * @returns {void}
   */
  update(_dt) {
    this._collectBlockers();

    for (const entity of this.world.query(Health, Transform, Weapon)) {
      if (!this._shouldShow(entity)) continue;
      this._place(entity, entity.get(Weapon));
    }

    for (const entity of [...this._active.keys()]) {
      if (!this._shouldShow(entity)) this._release(entity);
    }
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity Candidate carrier.
   * @returns {boolean} Whether it should currently show a beam.
   * @private
   */
  _shouldShow(entity) {
    if (!entity?.alive) return false;
    const weapon = entity.get(Weapon);
    if (!weapon?.laser || !weapon.enabled || weapon.owner !== entity) return false;
    const direction = weapon.aimDirection;
    return Number.isFinite(direction.x) && Number.isFinite(direction.z) && direction.lengthSq() > 1e-6;
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity Carrier.
   * @param {import('../components/Weapon.js').Weapon} weapon Equipped weapon.
   * @returns {void}
   * @private
   */
  _place(entity, weapon) {
    const beam = this._active.get(entity) ?? this._create(entity, weapon);
    const laser = weapon.laser;
    const direction = weapon.aimDirection;
    const length = this._beamLength(weapon);
    const width = Math.max(0.005, laser.width ?? DEFAULT_WIDTH);

    this._retint(beam, laser);
    beam.visible = true;
    beam.position.set(
      weapon.muzzle.x + direction.x * length * 0.5,
      weapon.muzzle.y,
      weapon.muzzle.z + direction.z * length * 0.5
    );
    beam.rotation.set(0, Math.atan2(direction.x, direction.z), 0);
    beam.scale.set(width, width, length);
  }

  /**
   * Caches this frame's static blockers. Walls come pre-merged into a few dozen
   * greedy rectangles rather than one box per tile, so a plain scan is cheaper
   * than any structure that would have to be rebuilt to hold them.
   *
   * @returns {void}
   * @private
   */
  _collectBlockers() {
    this._blockers.length = 0;
    for (const entity of this.world.query(Collider, Transform)) {
      if (entity.get(Collider).isStatic) this._blockers.push(entity);
    }
  }

  /**
   * How far the beam reaches before something stops it.
   *
   * A laser that sails through a wall the round cannot pass is a lie the player
   * aims by, so the beam is clipped to the first blocker its own round would
   * hit. Only static geometry counts: the Sniper pierces bodies, so ending the
   * line on an enemy would be the same lie in reverse.
   *
   * @param {import('../components/Weapon.js').Weapon} weapon Equipped weapon.
   * @returns {number} Beam length in world units.
   * @private
   */
  _beamLength(weapon) {
    const range = Math.max(0.01, weapon.range);
    const { x: ox, z: oz } = weapon.muzzle;
    const dx = weapon.aimDirection.x * range;
    const dz = weapon.aimDirection.z * range;

    // Bounds of the whole segment, to reject most blockers before any maths.
    const loX = Math.min(ox, ox + dx);
    const hiX = Math.max(ox, ox + dx);
    const loZ = Math.min(oz, oz + dz);
    const hiZ = Math.max(oz, oz + dz);

    // `raycastAABB` reports a fraction of the segment, so 1 means "unobstructed".
    let nearest = 1;
    // The round is a body, not a ray: `CollisionSystem` inflates every target by
    // the projectile's own half-extents so it can sweep a zero-width line. The
    // same padding here is what stops the beam from reaching a hair past the
    // point its round would actually burst.
    const pad = weapon.projectileRadius;

    for (const entity of this._blockers) {
      const collider = entity.get(Collider);
      if (!stopsRound(weapon, collider)) continue;

      const position = entity.get(Transform).position;
      const minX = position.x - collider.halfX - pad;
      const maxX = position.x + collider.halfX + pad;
      const minZ = position.z - collider.halfZ - pad;
      const maxZ = position.z + collider.halfZ + pad;
      if (maxX < loX || minX > hiX || maxZ < loZ || minZ > hiZ) continue;

      const t = raycastAABB(ox, oz, dx, dz, minX, minZ, maxX, maxZ);
      if (t >= 0 && t < nearest) nearest = t;
    }

    return Math.max(0.01, range * nearest);
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity Carrier.
   * @param {import('../components/Weapon.js').Weapon} weapon Equipped weapon.
   * @returns {THREE.Mesh} New beam object, already attached.
   * @private
   */
  _create(entity, weapon) {
    const laser = weapon.laser;
    const material = new THREE.MeshBasicMaterial({
      color: laser.color,
      transparent: true,
      opacity: clampOpacity(laser.opacity ?? DEFAULT_OPACITY),
      depthWrite: false,
      toneMapped: false,
      blending: THREE.AdditiveBlending
    });
    const beam = new THREE.Mesh(this._geometry, material);
    beam.name = 'SniperLaser';
    beam.castShadow = false;
    beam.receiveShadow = false;
    beam.renderOrder = RENDER_ORDER;
    beam.frustumCulled = false;
    beam.userData.laserColor = laser.color;
    beam.userData.laserOpacity = material.opacity;

    this._active.set(entity, beam);
    this.ctx.scene.add(beam);
    return beam;
  }

  /**
   * @param {THREE.Mesh} beam Beam object.
   * @param {import('../config/Weapons.js').LaserBlock} laser Visual settings.
   * @returns {void}
   * @private
   */
  _retint(beam, laser) {
    const material = /** @type {THREE.MeshBasicMaterial} */ (beam.material);
    const opacity = clampOpacity(laser.opacity ?? DEFAULT_OPACITY);
    if (beam.userData.laserColor !== laser.color) {
      material.color.setHex(laser.color);
      beam.userData.laserColor = laser.color;
    }
    if (beam.userData.laserOpacity !== opacity) {
      material.opacity = opacity;
      beam.userData.laserOpacity = opacity;
    }
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity Entity losing its beam.
   * @returns {void}
   * @private
   */
  _release(entity) {
    const beam = this._active.get(entity);
    if (!beam) return;
    this._active.delete(entity);
    beam.removeFromParent();
    /** @type {THREE.Material} */ (beam.material).dispose();
  }

  /** @returns {void} */
  dispose() {
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;

    for (const entity of [...this._active.keys()]) this._release(entity);
    this._geometry?.dispose();
    this._geometry = null;
  }
}

/**
 * Whether a collider would stop the round this weapon fires.
 *
 * This is {@link ../physics/Collision.js canCollide} rewritten against the
 * weapon's projectile layers, because the round does not exist yet — the beam
 * has to answer for a bullet that has not been spawned. Both directions of the
 * test matter: every dungeon prop is tagged `Layer.WALL`, but only real cover
 * carries the bullet layers in its own mask, so a one-sided check would end the
 * laser on a brazier or a gravestone that a shot sails straight past.
 *
 * @param {import('../components/Weapon.js').Weapon} weapon Firing weapon.
 * @param {import('../components/Collider.js').Collider} collider Candidate blocker.
 * @returns {boolean}
 */
function stopsRound(weapon, collider) {
  return (
    (weapon.projectileMask & collider.layer) !== 0 &&
    (collider.mask & weapon.projectileLayer) !== 0
  );
}

/**
 * @param {number} value Raw opacity.
 * @returns {number} Value clamped for Three.js material opacity.
 */
function clampOpacity(value) {
  return Math.max(0, Math.min(1, value));
}
